import { useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { NAV_GROUPS, type NavItem } from './navItems';
import { APP_VERSION } from '../version';
import { usePermissions } from '../shared/hooks/usePermissions';
import { useAuth } from '../features/auth/useAuth';
import { useLicense } from '../features/license/useLicense';
import { useInboxBadges } from '../features/whatsapp/contexts/useInboxBadges';
import type { Permission } from '../shared/types/permissions';
import styles from './Sidebar.module.css';

export type SidebarProps = {
  collapsed: boolean;
  onToggleCollapsed: () => void;
  isMobile: boolean;
  mobileOpen: boolean;
  onCloseMobile: () => void;
};

function NavItemLink({
  item,
  collapsed,
  badgeCount,
  onClick,
}: {
  item: NavItem;
  collapsed: boolean;
  badgeCount?: number;
  onClick?: () => void;
}) {
  const Icon = item.icon;
  const hasBadge = (badgeCount ?? 0) > 0;
  const badgeLabel = hasBadge ? (badgeCount! > 99 ? '99+' : String(badgeCount)) : '';
  return (
    <NavLink
      to={item.to}
      onClick={onClick}
      className={({ isActive }) => `${styles.item} ${isActive ? styles.active : ''}`}
      end={item.to === '/'}
      title={collapsed ? (hasBadge ? `${item.label} (${badgeLabel})` : item.label) : undefined}
    >
      <span className={styles['item-icon']}>
        <Icon size={18} />
        {hasBadge && collapsed ? (
          <span className={styles['item-dot']} aria-hidden="true" />
        ) : null}
      </span>
      <span className={styles['item-label']}>{item.label}</span>
      {hasBadge && !collapsed ? (
        <span
          className={styles['item-badge']}
          aria-label={`${badgeLabel} não lidas`}
        >
          {badgeLabel}
        </span>
      ) : null}
    </NavLink>
  );
}

function SidebarContent({
  collapsed,
  onItemClick,
}: {
  collapsed: boolean;
  onItemClick?: () => void;
}) {
  const { hasPermission, loaded } = usePermissions();
  const { userDoc } = useAuth();
  const { canUse } = useLicense();
  const inboxBadges = useInboxBadges();

  const visibleGroups = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => {
      if (item.requireProfessional && !userDoc?.professionalId) return false;
      if (item.module && !canUse(item.module)) return false;
      if (item.anyPermission) {
        if (!loaded) return false;
        return item.anyPermission.some((p) => hasPermission(p as Permission));
      }
      if (!item.permission) return true;
      // Enquanto permissions não carregaram, oculta items gateados
      if (!loaded) return false;
      return hasPermission(item.permission as Permission);
    }),
  })).filter((group) => group.items.length > 0);

  return (
    <nav className={styles.scroll} aria-label="Navegação principal">
      {visibleGroups.map((group) => (
        <div key={group.id} className={styles.group}>
          {group.label && !collapsed ? (
            <div className={styles['group-label']}>{group.label}</div>
          ) : null}
          {group.items.map((item) => (
            <NavItemLink
              key={item.id}
              item={item}
              collapsed={collapsed}
              badgeCount={item.id === 'whatsapp' ? inboxBadges.total : 0}
              onClick={onItemClick}
            />
          ))}
        </div>
      ))}
    </nav>
  );
}

export function Sidebar({
  collapsed,
  onToggleCollapsed,
  isMobile,
  mobileOpen,
  onCloseMobile,
}: SidebarProps) {
  // Trava scroll do body quando drawer aberto no mobile
  useEffect(() => {
    if (isMobile && mobileOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
    return undefined;
  }, [isMobile, mobileOpen]);

  if (isMobile) {
    return (
      <div
        className={`${styles.drawer} ${mobileOpen ? styles.open : ''}`}
        role="dialog"
        aria-label="Menu"
      >
        <div className={styles['drawer-backdrop']} onClick={onCloseMobile} />
        <aside className={styles['drawer-panel']}>
          <div className={styles['drawer-header']}>
            <span className={styles['drawer-brand']}>Menu</span>
            <button
              type="button"
              className={styles['drawer-close']}
              onClick={onCloseMobile}
              aria-label="Fechar menu"
            >
              <X size={18} />
            </button>
          </div>
          <SidebarContent collapsed={false} onItemClick={onCloseMobile} />
        </aside>
      </div>
    );
  }

  return (
    <aside className={`${styles.sidebar} ${collapsed ? styles.collapsed : ''}`}>
      <SidebarContent collapsed={collapsed} />
      <div className={styles.footer}>
        <button
          type="button"
          className={styles['collapse-btn']}
          onClick={onToggleCollapsed}
          aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          <span className={styles['collapse-label']}>Recolher</span>
        </button>
        <span className={styles.version}>v{APP_VERSION}</span>
      </div>
    </aside>
  );
}
